import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Grid3X3, Palette, Video, Camera, ChevronLeft, ChevronRight, Image, Play, Layers } from 'lucide-react';
import { PROJECTS } from '../data';
import MediaLightbox from '../components/MediaLightbox';
import PageHero from '../components/PageHero';

const PROJECTS_PER_PAGE = 9;

// ─── Main categories ───
const CATEGORY_VALUES = ['all', 'graphic', 'video', 'motion', 'photo'];

// ─── Sub-categories (only for video & photo) ───
const SUBCATEGORIES = {
  video: [
    { label: 'All', value: 'all' },
    { label: 'Corporate', value: 'corporate' },
    { label: 'Podcast', value: 'podcast' },
    { label: 'Politics', value: 'politics' },
    { label: 'Real Estate', value: 'real-estate' },
    { label: 'Social', value: 'social' },
    { label: 'Social Media', value: 'social-media' },
    { label: 'Wedding', value: 'wedding' },
  ],
  photo: [
    { label: 'All', value: 'all' },
    { label: 'Corporate', value: 'corporate' },
    { label: 'Graduation', value: 'graduation' },
    { label: 'Real Estate', value: 'real-estate' },
    { label: 'Social', value: 'social' },
    { label: 'Wedding', value: 'wedding' },
  ],
};

// Normalize a value: lowercase, trim, spaces → hyphens
// so "Real Estate" → "real-estate" matches sub-category values.
const normalize = (v) =>
  (v || '').toString().trim().toLowerCase().replace(/\s+/g, '-');

// ─── Fisher-Yates shuffle (returns a new array) ───
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// ─── Animation variants ───
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
});

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const pageButtonVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredId, setHoveredId] = useState(null);

  // ─── Lightbox state ───
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [combinedMedia, setCombinedMedia] = useState([]);
  const [mediaIndex, setMediaIndex] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  const categories = [
    { label: 'All', value: 'all', icon: Grid3X3 },
    { label: 'Graphic Design', value: 'graphic', icon: Palette },
    { label: 'Videography', value: 'video', icon: Video },
    { label: 'Motion Graphics', value: 'motion', icon: Layers },
    { label: 'Photography', value: 'photo', icon: Camera },
  ];

  const activeSubCategories = SUBCATEGORIES[filter]; // undefined for all/graphic/motion

  // ─── Read filter + subfilter from URL ───
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    const subParam = params.get('sub');

    if (filterParam && CATEGORY_VALUES.includes(filterParam)) {
      setFilter(filterParam);

      const subs = SUBCATEGORIES[filterParam];
      const validSub = subs?.some((s) => s.value === subParam);
      setSubFilter(validSub ? subParam : 'all');
    } else {
      setFilter('all');
      setSubFilter('all');
    }
  }, [location.search]);

  // ─── Update URL when filter / subfilter changes ───
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (filter === 'all') params.delete('filter');
    else params.set('filter', filter);

    if (subFilter === 'all' || !SUBCATEGORIES[filter]) params.delete('sub');
    else params.set('sub', subFilter);

    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, subFilter, navigate]);

  // ─── Reset page + lightbox when filter or subfilter changes ───
  useEffect(() => {
    setCurrentPage(1);
    if (lightboxOpen) {
      setLightboxOpen(false);
      setCombinedMedia([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, subFilter]);

  // ─── Base list for the active main category ───
  // - "All"  → mix every category together in random order
  // - single category → that category only, in its natural order
  const baseProjects = useMemo(() => {
    if (filter === 'all') return shuffleArray(PROJECTS);
    return PROJECTS.filter((p) => p.category === filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // ─── Filtered projects (category + subcategory) ───
  const filteredProjects = useMemo(() => {
    if (subFilter === 'all' || !SUBCATEGORIES[filter]) return baseProjects;
    return baseProjects.filter((p) => normalize(p.subcategory) === subFilter);
  }, [baseProjects, filter, subFilter]);

  // ─── Counts per subcategory (for badges) ───
  const subCounts = useMemo(() => {
    const counts = { all: baseProjects.length };
    baseProjects.forEach((p) => {
      const s = normalize(p.subcategory);
      if (s) counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [baseProjects]);

  const totalPages = Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PROJECTS_PER_PAGE;
  const currentProjects = filteredProjects.slice(startIndex, startIndex + PROJECTS_PER_PAGE);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ─── Handlers ───
  const handleCategoryChange = (value) => {
    setFilter(value);
    setSubFilter('all'); // always reset sub-tab when switching main category
  };

  // ─── Helpers for lightbox ───
  const getProjectMedia = (project) => {
    if (project.media && project.media.length > 0) return project.media;
    return [{ type: 'image', src: project.image, title: project.title }];
  };

  const openLightbox = (project) => {
    const allMedia = [];
    const mediaProjectIds = [];

    filteredProjects.forEach((p) => {
      const media = getProjectMedia(p);
      media.forEach((m) => {
        allMedia.push(m);
        mediaProjectIds.push(p.id);
      });
    });

    if (allMedia.length === 0) return;

    const startIdx = mediaProjectIds.indexOf(project.id);
    const finalIndex = startIdx !== -1 ? startIdx : 0;

    setCombinedMedia(allMedia);
    setMediaIndex(finalIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCombinedMedia([]);
  };

  const activeCategoryLabel = categories.find((c) => c.value === filter)?.label;
  const activeSubLabel = activeSubCategories?.find((s) => s.value === subFilter)?.label;

  return (
    <section className="min-h-screen py-24 bg-gradient-to-b from-black via-zinc-900 to-black pt-32 relative overflow-hidden">
      {/* ─── Background decorations ─── */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-600/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* ─── Page Hero ─── */}
        <PageHero
          badge="Portfolio"
          title="My <span class='text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500'>Work</span>"
          subtitle="Explore my projects across graphic design, videography, motion graphics, and photography – each crafted with passion and precision."
        >
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-gray-400">
            <span className="text-yellow-400 font-semibold">{filteredProjects.length}</span>
            <span>projects</span>
            {filter !== 'all' && (
              <span className="text-yellow-400">in {activeCategoryLabel}</span>
            )}
            {subFilter !== 'all' && activeSubLabel && (
              <span className="text-white/60">• {activeSubLabel}</span>
            )}
            {totalPages > 1 && (
              <span className="text-white/40">• Page {currentPage} of {totalPages}</span>
            )}
          </div>
        </PageHero>

        {/* ─── Main Filter Buttons ─── */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className={`flex flex-wrap justify-center gap-3 ${activeSubCategories ? 'mb-5' : 'mb-14'}`}
        >
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isActive = filter === cat.value;
            return (
              <motion.button
                key={cat.value}
                variants={fadeUp(0.05 * categories.indexOf(cat))}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => handleCategoryChange(cat.value)}
                className={`group relative px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  isActive
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/25'
                    : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/5'
                }`}
              >
                <Icon size={16} className={isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'} />
                {cat.label}
                {isActive && (
                  <motion.span
                    layoutId="activeFilterIndicator"
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-yellow-400"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </motion.div>

        {/* ─── Sub-category Tabs (Videography / Photography) ─── */}
        <AnimatePresence mode="wait">
          {activeSubCategories && (
            <motion.div
              key={`sub-tabs-${filter}`}
              initial={{ opacity: 0, y: -12, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -12, height: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap justify-center items-center gap-2 mb-14">
                {activeSubCategories.map((sub) => {
                  const isActive = subFilter === sub.value;
                  const count = subCounts[sub.value] ?? 0;
                  return (
                    <motion.button
                      key={sub.value}
                      onClick={() => setSubFilter(sub.value)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors duration-300 border ${
                        isActive
                          ? 'text-yellow-300 border-yellow-500/50'
                          : 'text-gray-400 border-white/10 bg-white/[0.03] hover:text-white hover:border-white/20'
                      }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId={`activeSubFilter-${filter}`}
                          className="absolute inset-0 rounded-full bg-yellow-500/15"
                          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10 flex items-center gap-1.5">
                        {sub.label}
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${
                            isActive
                              ? 'bg-yellow-500/25 text-yellow-200'
                              : 'bg-white/5 text-gray-500'
                          }`}
                        >
                          {count}
                        </span>
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Projects Grid ─── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${filter}-${subFilter}-${currentPage}`}
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {currentProjects.map((project, index) => {
              const isVideo = project.category === 'video' || project.category === 'motion';
              return (
                <motion.div
                  key={project.id}
                  variants={fadeUp(0.08 * index)}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-yellow-500/40 transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-500/10 cursor-pointer"
                  onMouseEnter={() => setHoveredId(project.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => openLightbox(project)}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden">
                    <motion.img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                      initial={false}
                      animate={{ scale: hoveredId === project.id ? 1.1 : 1 }}
                      transition={{ duration: 0.6 }}
                      loading="lazy"
                    />
                    {/* ─── Preview hint (only for images) ─── */}
                    {!isVideo && (
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <span className="px-4 py-2 rounded-full bg-yellow-500/80 text-white text-sm font-medium backdrop-blur-sm flex items-center gap-2">
                          <Image size={16} /> Preview
                        </span>
                      </div>
                    )}
                    {/* ─── Play icon (only for videos) ─── */}
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-14 h-14 rounded-full bg-yellow-500/80 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                          <Play size={24} className="text-white ml-1" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ─── Overlay ─── */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      {!isVideo && (
                        <div className="mt-3 text-yellow-300 text-sm font-medium flex items-center gap-1">
                          Click to preview
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Category badge */}
                  <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-gray-300 border border-white/10 pointer-events-none">
                    {categories.find((c) => c.value === project.category)?.label || project.category}
                  </div>

                  {/* Subcategory badge (only for video / photo) */}
                  {SUBCATEGORIES[project.category] && project.subcategory && (
                    <div className="absolute top-4 right-4 bg-yellow-500/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-yellow-300 border border-yellow-500/30 pointer-events-none">
                      {project.subcategory}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* ─── Empty State ─── */}
        {filteredProjects.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <p className="text-gray-400 text-lg">No projects found in this category.</p>
          </motion.div>
        )}

        {/* ─── Pagination ─── */}
        {totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center items-center gap-2 mt-14"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-yellow-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={18} />
            </motion.button>
            <AnimatePresence mode="wait">
              {getPageNumbers().map((page) => (
                <motion.button
                  key={page}
                  variants={pageButtonVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => goToPage(page)}
                  className={`px-4 py-2 rounded-full text-sm transition ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/25'
                      : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/5'
                  }`}
                >
                  {page}
                </motion.button>
              ))}
            </AnimatePresence>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-yellow-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={18} />
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* ─── Lightbox ─── */}
      {lightboxOpen && combinedMedia.length > 0 && (
        <MediaLightbox
          media={combinedMedia}
          initialIndex={mediaIndex}
          onClose={closeLightbox}
        />
      )}
    </section>
  );
};

export default Portfolio;